<template>
  <div class="service-visit-page">
    <KTCard ref="preview" v-bind:title="'Show'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-service-visits' }">{{
            $t("MENU.ITEM.SERVICE.VISITS")
          }}</router-link>
          <i class="mr-2"></i>
          <small>{{ $t("MACHINE_SERVICE_SIGNALS.SHOW") }}</small>
        </h3>
      </template>
      <template v-slot:toolbar>
        <div
          class="service-visit-page__actions service-visit-show__actions"
          :class="{ 'service-visit-show__actions--with-edit': canEditVisit }"
        >
          <BackButton class="service-visit-show__back-action" />
          <router-link
            v-if="canEditVisit"
            :to="{ name: 'edit-service-visits', params: { id: item.id } }"
            class="btn btn-light-primary font-weight-bolder mr-2"
          >
            <i class="flaticon-edit"></i>{{ $t("LABELS.EDIT") }}
          </router-link>
          <button
            v-if="canManageStatus"
            type="button"
            class="btn btn-light-success font-weight-bolder mr-2"
            :disabled="!isStartWorkEnabled"
            @click="startWork"
          >
            {{ $t("SERVICE_VISITS.START_WORK") }}
          </button>
          <button
            v-if="canManageStatus"
            type="button"
            class="btn btn-success font-weight-bolder"
            :disabled="!isFinishVisitEnabled"
            @click="finishServiceVisit"
          >
            {{ $t("SERVICE_VISITS.FINISH_VISIT") }}
          </button>
        </div>
      </template>
      <template v-slot:body>
        <v-skeleton-loader v-if="isPageLoading" type="article" />
        <div v-else-if="item">
          <b-row>
            <b-col md="6">
              <p><strong>{{ $t("FORM_LABELS.MACHINE") }}:</strong> {{ machineName(item.machine) }}</p>
              <b-form-group
                v-if="canChangeStatus"
                class="service-visit-status-field"
                :label="$t('FORM_LABELS.STATUS')"
              >
                <FormSelect
                  :value="item.statusId"
                  :options="statuses"
                  :disabled="isChangingStatus"
                  @input="changeStatus"
                />
              </b-form-group>
              <p v-else><strong>{{ $t("FORM_LABELS.STATUS") }}:</strong> {{ item.status ? item.status.name : "" }}</p>
              <p><strong>{{ $t("FORM_LABELS.PRIORITY") }}:</strong> {{ item.priority ? item.priority.name : "" }}</p>
              <p><strong>{{ $t("FORM_LABELS.IS_ON_ROAD") }}:</strong> {{ isOnRoad ? $t("LABELS.YES") : $t("LABELS.NO") }}</p>
              <p v-if="isOnRoad && item.onRoadNote"><strong>{{ $t("FORM_LABELS.ON_ROAD_NOTE") }}:</strong> {{ item.onRoadNote }}</p>
            </b-col>
            <b-col md="6">
              <p><strong>{{ $t("FORM_LABELS.LOCATION") }}:</strong> {{ locationText }}</p>
              <p><strong>{{ $t("FORM_LABELS.DATE_FROM") }}:</strong> {{ item.dateFrom }}</p>
              <p><strong>{{ $t("FORM_LABELS.DATE_TO") }}:</strong> {{ item.dateTo }}</p>
              <p><strong>{{ $t("FORM_LABELS.DURATION") }}:</strong> {{ item.duration !== null && item.duration !== undefined ? item.duration : "-" }}</p>
              <p v-if="item.serviceMileage !== null && item.serviceMileage !== undefined"><strong>{{ serviceMileageLabel }}:</strong> {{ item.serviceMileage }}</p>
              <p v-if="item.hoursWorked !== null && item.hoursWorked !== undefined"><strong>{{ $t("SERVICE_VISITS.HOURS_WORKED") }}:</strong> {{ item.hoursWorked }}</p>
              <p v-if="item.workDone"><strong>{{ $t("SERVICE_VISITS.WORK_DONE") }}:</strong> {{ item.workDone }}</p>
            </b-col>
          </b-row>

          <div class="separator separator-dashed my-6"></div>

          <h5>{{ $t("MENU.ITEM.SERVICE.MECHANICS") }}</h5>
          <p>{{ mechanicNames(item.mechanics) || "-" }}</p>

          <div class="d-flex align-items-center justify-content-between mt-6 mb-3">
            <h5 class="mb-0">{{ $t("SERVICE_VISITS.TASKS") }}</h5>
            <button
              v-if="canEditTasks"
              type="button"
              class="btn btn-light-success font-weight-bolder"
              :disabled="Boolean(newCustomTask)"
              @click="addCustomTask"
            >
              <i class="flaticon2-plus"></i>{{ $t("SERVICE_VISITS.ADD_TASK") }}
            </button>
          </div>
          <ServiceVisitCustomTaskRow
            v-if="newCustomTask"
            :task="newCustomTask"
            :item-statuses="itemStatuses"
            :saving="isSavingTask"
            show-save
            @save="saveCustomTask"
            @cancel="cancelCustomTask"
          />
          <v-simple-table class="service-visit-tasks-table">
            <thead>
              <tr>
                <th>{{ $t("FORM_LABELS.DESCRIPTION") }}</th>
                <th>{{ $t("FORM_LABELS.STATUS") }}</th>
                <th>{{ $t("FORM_LABELS.NOTE") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="task in item.items" :key="task.id">
                <td>{{ task.name }}</td>
                <td class="service-visit-task-status-cell">
                  <FormSelect
                    v-if="canEditTaskStatuses"
                    :value="task.statusId"
                    :options="itemStatuses"
                    :disabled="isTaskStatusSaving(task.id)"
                    append-to-body
                    @input="updateTaskStatus(task, $event)"
                  />
                  <span v-else>{{ task.status ? task.status.name : "" }}</span>
                </td>
                <td class="white-space-pre-wrap">{{ task.note || "-" }}</td>
              </tr>
              <tr v-if="!item.items || !item.items.length">
                <td colspan="3">-</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="separator separator-dashed my-6"></div>

          <div class="d-flex align-items-center justify-content-between">
            <h5 class="mb-0">{{ $t("SERVICE_VISITS.HISTORY.TITLE") }}</h5>
            <b-button
              v-if="canCreateComment"
              type="button"
              variant="light-success"
              class="font-weight-bolder"
              @click="openCommentModal()"
            >
              <i class="flaticon2-plus"></i>{{ $t("SERVICE_VISITS.HISTORY.ADD_COMMENT") }}
            </b-button>
          </div>
          <div v-if="timeline.length" class="service-visit-history">
            <div
              v-for="entry in timeline"
              :key="entry.key"
              class="service-visit-history__entry border-bottom py-3"
            >
              <template v-if="entry.type === 'history'">
                <div class="font-weight-bolder">{{ historySummary(entry.entry) }}</div>
                <div class="font-size-sm">
                  {{ historyActor(entry.entry) }} · {{ historyDate(entry.entry) }}
                </div>
                <ul v-if="historyChanges(entry.entry).length" class="mb-0 mt-2 pl-5">
                  <li v-for="(change, index) in historyChanges(entry.entry)" :key="index">
                    {{ historyChangeText(change) }}
                  </li>
                </ul>
                <div v-else-if="historySnapshotText(entry.entry)" class="font-size-sm mt-2">
                  {{ historySnapshotText(entry.entry) }}
                </div>
              </template>
              <template v-else>
                <div class="d-flex justify-content-between align-items-start">
                  <div class="font-weight-bolder">{{ $t("SERVICE_VISITS.HISTORY.COMMENT") }}</div>
                  <div v-if="canEditComment(entry.entry) || canDeleteComment(entry.entry)" class="d-flex flex-shrink-0 ml-2">
                    <v-tooltip v-if="canEditComment(entry.entry)" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on="on" @click="openCommentModal(entry.entry)">
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("LABELS.EDIT") }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="canDeleteComment(entry.entry)" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon color="error" v-bind="attrs" v-on="on" @click="deleteComment(entry.entry)">
                          <v-icon>mdi-trash-can</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("LABELS.DELETE") }}</span>
                    </v-tooltip>
                  </div>
                </div>
                <div class="font-size-sm">
                  {{ entry.entry.authorName || entry.entry.author_name }} · {{ historyDate({ occurredAt: entry.entry.createdAt || entry.entry.created_at }) }}
                  <span v-if="entry.entry.status && entry.entry.status.name"> · {{ entry.entry.status.name }}</span>
                  <span v-if="isCommentEdited(entry.entry)"> · {{ $t("SERVICE_VISITS.HISTORY.EDITED") }}</span>
                </div>
                <div class="white-space-pre-wrap mt-2">
                  {{ entry.entry.isDeleted || entry.entry.is_deleted ? $t("SERVICE_VISITS.HISTORY.COMMENT_REMOVED") : entry.entry.body }}
                </div>
              </template>
            </div>
          </div>
          <p v-else>-</p>

        </div>
      </template>
    </KTCard>
    <ServiceVisitCompletionModal
      ref="completionModal"
      modal-id="finish-service-visit-from-show-modal"
      :visit="item"
      :saving="isChangingStatus"
      @complete="completeServiceVisit"
      @hidden="resetFinishVisitModal"
    />
    <v-dialog v-model="isCommentModalOpen" max-width="600px" :persistent="isSavingComment">
      <v-card>
        <v-card-title class="d-flex justify-content-between align-items-center">
          <span>{{ editingComment ? $t("SERVICE_VISITS.HISTORY.EDIT_COMMENT") : $t("SERVICE_VISITS.HISTORY.ADD_COMMENT") }}</span>
          <v-btn icon :disabled="isSavingComment" @click="closeCommentModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <b-form-textarea v-model="commentBody" rows="5" max-rows="8" :disabled="isSavingComment" />
        </v-card-text>
        <v-card-actions>
          <v-btn color="error" text :disabled="isSavingComment" @click="closeCommentModal">{{ $t("BUTTON.CANCEL") }}</v-btn>
          <v-spacer></v-spacer>
          <SaveButton :isLoading="isSavingComment" :disabled="!commentBody.trim()" @save="saveComment" />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  CREATE_SERVICE_VISIT_TASK_ITEM,
  CREATE_SERVICE_VISIT_COMMENT_FOR_VISIT,
  DELETE_SERVICE_VISIT_COMMENT_FOR_VISIT,
  FETCH_SERVICE_VISIT,
  FETCH_SERVICE_VISIT_ITEM_STATUSES,
  FETCH_SERVICE_VISIT_STATUSES,
  MOVE_SERVICE_VISIT_BOARD_CARD,
  UPDATE_SERVICE_VISIT_COMMENT_FOR_VISIT,
  UPDATE_SERVICE_VISIT_TASK_ITEM
} from "@/modules/service-visits/store/service-visits.module";
import { permissionMixin } from "@/core/mixins/permissionMixin.js";
import KTCard from "@/view/content/Card.vue";
import FormSelect from "@/view/content/forms/components/FormSelect.vue";
import SaveButton from "@/view/content/forms/components/SaveButton.vue";
import ServiceVisitCompletionModal from "@/modules/service-visits/components/ServiceVisitCompletionModal.vue";
import ServiceVisitCustomTaskRow from "@/modules/service-visits/components/ServiceVisitCustomTaskRow.vue";
import {
  areServiceVisitTasksTerminal,
  isServiceVisitFinished,
  isServiceVisitOnRoad,
  serviceVisitLocationText,
  serviceVisitResponseErrorMessage
} from "../services/service-visit.mapper";

export default {
  name: "ServiceVisitsShow",
  mixins: [permissionMixin],
  components: {
    KTCard,
    FormSelect,
    SaveButton,
    ServiceVisitCompletionModal,
    ServiceVisitCustomTaskRow
  },
  data() {
    return {
      isPageLoading: true,
      item: null,
      commentBody: "",
      editingComment: null,
      isSavingComment: false,
      isCommentModalOpen: false,
      itemStatuses: [],
      newCustomTask: null,
      isSavingTask: false,
      statuses: [],
      isChangingStatus: false,
      finishVisitStatusId: null,
      savingTaskStatusIds: []
    };
  },
  computed: {
    isOnRoad() {
      return isServiceVisitOnRoad(this.item);
    },
    locationText() {
      return serviceVisitLocationText(
        this.item,
        this.$t("FORM_LABELS.IS_ON_ROAD")
      );
    },
    canEditVisit() {
      return (
        this.item &&
        !isServiceVisitFinished(this.item) &&
        this.checkIfUserHasPermission(["service-visits-update"])
      );
    },
    canManageStatus() {
      return Boolean(
        this.item &&
        this.statuses.length &&
        this.item.canManageBoard &&
        this.checkIfUserHasPermission(["service-visits-update", "mechanic"])
      );
    },
    canChangeStatus() {
      return this.canManageStatus && !isServiceVisitFinished(this.item);
    },
    currentStatusSlug() {
      return this.item && this.item.status ? this.item.status.slug : null;
    },
    inProgressStatus() {
      return this.statuses.find(status => status.slug === "in-progress") || null;
    },
    finishedStatus() {
      return this.statuses.find(status => status.slug === "finished") || null;
    },
    isStartWorkEnabled() {
      return Boolean(
        this.canChangeStatus &&
        !this.isChangingStatus &&
        this.currentStatusSlug === "new" &&
        this.inProgressStatus
      );
    },
    isFinishVisitEnabled() {
      return Boolean(
        this.canChangeStatus &&
        !this.isChangingStatus &&
        !this.isSavingTask &&
        !this.savingTaskStatusIds.length &&
        !this.newCustomTask &&
        this.currentStatusSlug === "in-progress" &&
        this.finishedStatus &&
        areServiceVisitTasksTerminal(this.item)
      );
    },
    canManageVisitComments() {
      return this.checkIfUserHasPermission(["transport-manager", "mechanic"]);
    },
    canCreateComment() {
      return this.item && !isServiceVisitFinished(this.item)
        && this.canManageVisitComments;
    },
    canEditTasks() {
      return Boolean(
        this.item
        && !isServiceVisitFinished(this.item)
        && this.item.canManageBoard
      );
    },
    canEditTaskStatuses() {
      return this.canEditTasks && this.itemStatuses.length > 0;
    },
    timeline() {
      const history = (this.item && this.item.history ? this.item.history : []).map(entry => ({
        key: `history-${entry.id}`,
        type: "history",
        entry,
        occurredAt: entry.occurredAt || entry.occurred_at
      }));
      const comments = (this.item && this.item.comments ? this.item.comments : []).map(entry => ({
        key: `comment-${entry.id}`,
        type: "comment",
        entry,
        occurredAt: entry.createdAt || entry.created_at
      }));

      return [...history, ...comments].sort((first, second) =>
        new Date(second.occurredAt.replace(" ", "T")) - new Date(first.occurredAt.replace(" ", "T"))
      );
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.SERVICE.VISITS"),
        route: { name: "list-service-visits" }
      },
      { title: this.$i18n.t("MACHINE_SERVICE_SIGNALS.SHOW") }
    ]);
    this.loadStatuses();
    this.getServiceVisit();
  },
  methods: {
    loadStatuses() {
      if (!this.checkIfUserHasPermission(["service-visits-update", "mechanic"])) {
        return;
      }

      this.$store.dispatch(FETCH_SERVICE_VISIT_STATUSES)
        .then(data => {
          this.statuses = data.data || [];
        })
        .catch(response => this.showStatusError(response));
    },
    getServiceVisit() {
      return this.$store.dispatch(FETCH_SERVICE_VISIT, this.$route.params.id).then(data => {
        this.item = data.data;
        this.isPageLoading = false;

        if (this.canEditTasks) {
          return this.loadItemStatuses();
        }
      });
    },
    loadItemStatuses() {
      if (this.itemStatuses.length) {
        return Promise.resolve(true);
      }

      return this.$store.dispatch(FETCH_SERVICE_VISIT_ITEM_STATUSES)
        .then(data => {
          this.itemStatuses = data.data || [];
          return true;
        })
        .catch(response => {
          this.showTaskError(response);
          return false;
        });
    },
    updateTaskStatus(task, statusId) {
      if (
        !this.canEditTasks ||
        !statusId ||
        Number(statusId) === Number(task.statusId) ||
        this.isTaskStatusSaving(task.id)
      ) {
        return;
      }

      this.savingTaskStatusIds.push(task.id);

      this.$store.dispatch(UPDATE_SERVICE_VISIT_TASK_ITEM, {
        itemId: task.id,
        payload: { statusId }
      })
        .then(response => {
          this.replaceTaskItem(response.data);
          this.$notify({
            group: "notify",
            type: "success",
            title: `<i class='flaticon2-checkmark'></i> ${this.$t("ALERTS.SUCCESS")}`,
            text: this.$t("SERVICE_VISITS.TASK_STATUS_UPDATED")
          });
          return this.refreshServiceVisit();
        }, response => this.showTaskError(response))
        .finally(() => {
          this.savingTaskStatusIds = this.savingTaskStatusIds.filter(
            itemId => Number(itemId) !== Number(task.id)
          );
        });
    },
    replaceTaskItem(updatedTask) {
      const taskIndex = (this.item.items || []).findIndex(
        task => Number(task.id) === Number(updatedTask.id)
      );

      if (taskIndex >= 0) {
        this.$set(this.item.items, taskIndex, updatedTask);
      }
    },
    isTaskStatusSaving(taskId) {
      return this.savingTaskStatusIds.some(
        itemId => Number(itemId) === Number(taskId)
      );
    },
    startWork() {
      if (this.isStartWorkEnabled) {
        this.changeStatus(this.inProgressStatus.id);
      }
    },
    finishServiceVisit() {
      if (this.isFinishVisitEnabled) {
        this.changeStatus(this.finishedStatus.id);
      }
    },
    changeStatus(statusId) {
      if (
        !this.canChangeStatus ||
        !statusId ||
        Number(statusId) === Number(this.item.statusId)
      ) {
        return;
      }

      const status = this.statuses.find(item => Number(item.id) === Number(statusId));

      if (status && status.slug === "finished") {
        if (!areServiceVisitTasksTerminal(this.item)) {
          this.showStatusError(null, this.$t("SERVICE_VISITS.TASKS_MUST_BE_TERMINAL"));
          return;
        }

        this.finishVisitStatusId = statusId;
        this.$nextTick(() => this.$refs.completionModal.open());
        return;
      }

      this.submitStatusChange({
        visitId: this.item.id,
        statusId,
        visitAboveId: null,
        visitBelowId: null
      });
    },
    completeServiceVisit(completionDetails) {
      if (!this.finishVisitStatusId) {
        return;
      }

      this.submitStatusChange({
        visitId: this.item.id,
        statusId: this.finishVisitStatusId,
        visitAboveId: null,
        visitBelowId: null,
        ...completionDetails
      }).then(updated => {
        if (updated) {
          this.$refs.completionModal.close();
        }
      });
    },
    submitStatusChange(payload) {
      this.isChangingStatus = true;

      return this.$store.dispatch(MOVE_SERVICE_VISIT_BOARD_CARD, payload)
        .then(() => {
          this.applyVisitStatus(payload);
          this.$notify({
            group: "notify",
            type: "success",
            title: `<i class='flaticon2-checkmark'></i> ${this.$t("ALERTS.SUCCESS")}`,
            text: this.$t("SERVICE_VISITS.STATUS_UPDATED")
          });
          return this.refreshServiceVisit().then(() => true);
        }, response => {
          this.showStatusError(response);
          return false;
        })
        .finally(() => {
          this.isChangingStatus = false;
        });
    },
    applyVisitStatus(payload) {
      const status = this.statuses.find(
        item => Number(item.id) === Number(payload.statusId)
      );

      this.item = {
        ...this.item,
        statusId: payload.statusId,
        status: status || this.item.status,
        ...(Object.prototype.hasOwnProperty.call(payload, "serviceMileage")
          ? { serviceMileage: payload.serviceMileage }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(payload, "hoursWorked")
          ? { hoursWorked: payload.hoursWorked }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(payload, "workDone")
          ? { workDone: payload.workDone }
          : {})
      };
    },
    refreshServiceVisit() {
      return this.getServiceVisit().catch(() => {
        this.$notify({
          group: "notify",
          type: "error",
          title: this.$t("ALERTS.ERROR"),
          text: this.$t("SERVICE_VISITS.LIST_LOAD_ERROR")
        });
      });
    },
    resetFinishVisitModal() {
      this.finishVisitStatusId = null;
    },
    showStatusError(response, fallback = null) {
      const message = serviceVisitResponseErrorMessage(response);

      this.$notify({
        group: "notify",
        type: "error",
        title: `<i class='flaticon-close'></i> ${this.$t("ALERTS.WARNING")}`,
        text: message || fallback || this.$t("SERVICE_VISITS.STATUS_UPDATE_FAILED")
      });
    },
    addCustomTask() {
      if (!this.canEditTasks || this.newCustomTask) {
        return;
      }

      const createDraft = () => {
        const newStatus = this.itemStatuses.find(status => status.slug === "new");

        this.newCustomTask = {
          description: "",
          statusId: newStatus ? newStatus.id : (this.itemStatuses[0] || {}).id || null,
          note: ""
        };
      };

      this.loadItemStatuses().then(loaded => {
        if (loaded) {
          createDraft();
        }
      });
    },
    cancelCustomTask() {
      if (!this.isSavingTask) {
        this.newCustomTask = null;
      }
    },
    saveCustomTask() {
      if (!this.canEditTasks || !this.newCustomTask) {
        return;
      }

      this.isSavingTask = true;

      this.$store.dispatch(CREATE_SERVICE_VISIT_TASK_ITEM, {
        visitId: this.item.id,
        payload: this.newCustomTask
      })
        .then(() => {
          this.newCustomTask = null;
          this.$notify({
            group: "notify",
            type: "success",
            title: `<i class='flaticon2-checkmark'></i> ${this.$t("ALERTS.SUCCESS")}`,
            text: this.$t("SERVICE_VISITS.TASK_CREATED")
          });
          return this.getServiceVisit();
        })
        .catch(response => this.showTaskError(response))
        .finally(() => {
          this.isSavingTask = false;
        });
    },
    showTaskError(response) {
      this.$notify({
        group: "notify",
        type: "error",
        title: this.$t("ALERTS.ERROR"),
        text: (response && response.data && response.data.message)
          || this.$t("SERVICE_VISITS.TASK_UPDATE_FAILED")
      });
    },
    openCommentModal(comment = null) {
      this.editingComment = comment;
      this.commentBody = comment && comment.body ? comment.body : "";
      this.isCommentModalOpen = true;
    },
    closeCommentModal() {
      if (this.isSavingComment) return;
      this.isCommentModalOpen = false;
      this.resetCommentModal();
    },
    resetCommentModal() {
      this.editingComment = null;
      this.commentBody = "";
      this.isSavingComment = false;
    },
    saveComment() {
      this.isSavingComment = true;
      const action = this.editingComment
        ? UPDATE_SERVICE_VISIT_COMMENT_FOR_VISIT
        : CREATE_SERVICE_VISIT_COMMENT_FOR_VISIT;
      const payload = this.editingComment
        ? {
          visitId: this.item.id,
          commentId: this.editingComment.id,
          payload: {
            body: this.commentBody,
            statusId: this.editingComment.statusId
          }
        }
        : { visitId: this.item.id, payload: { body: this.commentBody } };

      this.$store.dispatch(action, payload)
        .then(data => {
          this.isCommentModalOpen = false;
          this.resetCommentModal();
          this.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });
          return this.getServiceVisit();
        })
        .catch(response => this.$notify({
          group: "notify",
          type: "error",
          title: this.$t("ALERTS.ERROR"),
          text: response.data.message
        }))
        .finally(() => { this.isSavingComment = false; });
    },
    deleteComment(comment) {
      this.$dialog
        .confirm(this.$t("BUTTON.PLEASE_CONFIRM"))
        .then(() => {
          this.$store.dispatch(DELETE_SERVICE_VISIT_COMMENT_FOR_VISIT, { visitId: this.item.id, commentId: comment.id })
            .then(data => {
              this.$notify({
                group: "notify",
                type: "success",
                title: "<i class='flaticon2-checkmark'></i> Success",
                text: data.message
              });
              return this.getServiceVisit();
            })
            .catch(response => this.$notify({
              group: "notify",
              type: "error",
              title: this.$t("ALERTS.ERROR"),
              text: response.data.message
            }));
        })
        .catch(() => {});
    },
    canManageComment(comment) {
      if (comment.isDeleted || comment.is_deleted || isServiceVisitFinished(this.item)) return false;
      return this.canManageVisitComments;
    },
    canEditComment(comment) {
      return this.canManageComment(comment);
    },
    canDeleteComment(comment) {
      return this.canManageComment(comment);
    },
    isCommentEdited(comment) {
      const createdAt = comment.createdAt || comment.created_at;
      const updatedAt = comment.updatedAt || comment.updated_at;
      return createdAt && updatedAt && createdAt !== updatedAt;
    },
    machineName(machine) {
      return machine ? machine.serviceDisplayName || machine.name || "" : "";
    },
    mechanicNames(mechanics) {
      return (mechanics || [])
        .map(mechanic => `${mechanic.firstName || ""} ${mechanic.lastName || ""}`.trim())
        .join(", ");
    },
    historyDetails(entry) {
      return entry.details || {};
    },
    historyChanges(entry) {
      return this.historyDetails(entry).changes || [];
    },
    historySummary(entry) {
      const event = entry.event;
      if (event === "created") return this.$t("SERVICE_VISITS.HISTORY.CREATED");
      if (event === "deleted") return this.$t("SERVICE_VISITS.HISTORY.DELETED");
      return this.$t("SERVICE_VISITS.HISTORY.UPDATED");
    },
    historyActor(entry) {
      return entry.actorName || entry.actor_name || this.$t("SERVICE_VISITS.HISTORY.SYSTEM");
    },
    historyDate(entry) {
      const value = entry.occurredAt || entry.occurred_at;
      return value ? new Date(value.replace(" ", "T")).toLocaleString() : "";
    },
    historyValue(value) {
      if (value === null || value === undefined || value === "") return "—";
      if (typeof value === "boolean") return value ? this.$t("LABELS.YES") : this.$t("LABELS.NO");
      if (typeof value === "object") return value.name || "—";
      return String(value);
    },
    historyChangeText(change) {
      const labels = {
        machine: "MACHINE", status: "STATUS", priority: "PRIORITY", is_on_road: "IS_ON_ROAD", isOnRoad: "IS_ON_ROAD",
        on_road_note: "ON_ROAD_NOTE", onRoadNote: "ON_ROAD_NOTE", service_location: "SERVICE_LOCATION", serviceLocation: "SERVICE_LOCATION",
        date_from: "DATE_FROM", dateFrom: "DATE_FROM", date_to: "DATE_TO", dateTo: "DATE_TO", duration: "DURATION",
        service_mileage: "SERVICE_MILEAGE", serviceMileage: "SERVICE_MILEAGE", hours_worked: "HOURS_WORKED", hoursWorked: "HOURS_WORKED", work_done: "WORK_DONE", workDone: "WORK_DONE", name: "NAME", note: "NOTE"
      };
      const fieldLabel = field => this.$t(`SERVICE_VISITS.HISTORY.FIELDS.${labels[field] || field}`);
      if (change.kind === "mechanic_added") return this.$t("SERVICE_VISITS.HISTORY.MECHANIC_ADDED", { name: this.historyValue(change.mechanic) });
      if (change.kind === "mechanic_removed") return this.$t("SERVICE_VISITS.HISTORY.MECHANIC_REMOVED", { name: this.historyValue(change.mechanic) });
      if (change.kind === "task_added") return this.$t("SERVICE_VISITS.HISTORY.TASK_ADDED", { name: this.historyValue(change.task) });
      if (change.kind === "task_removed") return this.$t("SERVICE_VISITS.HISTORY.TASK_REMOVED", { name: this.historyValue(change.task) });
      if (change.kind === "task_updated") {
        const taskChanges = change.changes
          ? Object.entries(change.changes).map(([field, values]) => this.$t("SERVICE_VISITS.HISTORY.FIELD_CHANGED", {
            field: fieldLabel(field),
            before: this.historyValue(values.before),
            after: this.historyValue(values.after)
          })).join("; ")
          : (change.fields || []).map(field => this.$t("SERVICE_VISITS.HISTORY.FIELD_CHANGED", {
            field: fieldLabel(field),
            before: this.historyValue(change.before && change.before[field]),
            after: this.historyValue(change.after && change.after[field])
          })).join("; ");

        return this.$t("SERVICE_VISITS.HISTORY.TASK_UPDATED", {
          name: this.historyValue(change.task || change.after),
          changes: taskChanges
        });
      }
      return this.$t("SERVICE_VISITS.HISTORY.FIELD_CHANGED", {
        field: fieldLabel(change.field), before: this.historyValue(change.before), after: this.historyValue(change.after)
      });
    },
    historySnapshotText(entry) {
      const snapshot = this.historyDetails(entry).after || this.historyDetails(entry).before;
      if (!snapshot) return "";
      return [
        snapshot.machine && this.$t("SERVICE_VISITS.HISTORY.SNAPSHOT_MACHINE", { name: this.historyValue(snapshot.machine) }),
        snapshot.status && this.$t("SERVICE_VISITS.HISTORY.SNAPSHOT_STATUS", { name: this.historyValue(snapshot.status) }),
        snapshot.priority && this.$t("SERVICE_VISITS.HISTORY.SNAPSHOT_PRIORITY", { name: this.historyValue(snapshot.priority) }),
        (snapshot.date_from || snapshot.dateFrom) && this.$t("SERVICE_VISITS.HISTORY.SNAPSHOT_PERIOD", { from: snapshot.date_from || snapshot.dateFrom, to: snapshot.date_to || snapshot.dateTo }),
        snapshot.mechanics && this.$t("SERVICE_VISITS.HISTORY.SNAPSHOT_MECHANICS", { names: (snapshot.mechanics || []).map(mechanic => this.historyValue(mechanic)).join(", ") || "—" })
      ].filter(Boolean).join(" · ");
    },
  }
};
</script>

<style scoped>
.white-space-pre-wrap {
  white-space: pre-wrap;
}

.service-visit-status-field {
  max-width: 22rem;
}

.service-visit-tasks-table ::v-deep table {
  min-width: 40rem;
}

.service-visit-task-status-cell {
  min-width: 11rem;
  width: 14rem;
}

@media (max-width: 767.98px) {
  .service-visit-page .service-visit-page__actions.service-visit-show__actions {
    grid-auto-flow: row;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .service-visit-page .service-visit-page__actions.service-visit-show__actions .btn {
    line-height: 1.2;
    min-height: 3rem;
    white-space: normal;
  }

  .service-visit-show__back-action {
    grid-column: 1 / -1;
  }

  .service-visit-show__actions--with-edit .service-visit-show__back-action {
    grid-column: auto;
  }
}
</style>
